import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { usePortfolio, Product } from "@/services/ApiOps";

import { IndividualProduct } from "@pages/portfolio/components/IndividualProduct";
import CategoryGrid from "@pages/portfolio/components/CategoriesGrid";

const categoriesReference: { name: string; subcategories?: string[] }[] = [
  {
    name: "Válvulas",
    subcategories: [
      "Válvulas Gaveta",
      "Válvulas Globo",
      "Válvulas de Retenção",
      "Valvulas de Esfera",
      "Válvulas Borboleta",
    ],
  },
  {
    name: "Atuadores Pneumáticos",
    subcategories: ["Dupla Ação", "Simples Ação", "Pneumatico e outros"],
  },
  { name: "Automação e Controle" },
  {
    name: "Sensores e Gateways para Monitoramento Industrial",
    subcategories: ["Gateways de Comunicação", "Sensores de Monitoramento"],
  },
  { name: "Conexões e Flanges para Tubulação" },
  { name: "Painéis Elétricos" },
];

const ProductsPreviewer: React.FC = () => {
  const { data: products = [], isLoading, isError } = usePortfolio();
  const [showCategoryGrid, setShowCategoryGrid] = useState(() => {
    return !sessionStorage.getItem("hasVisitedPortfolio");
  });

  const [selectedCategory, setSelectedCategory] = useState(() => {
    const saved = sessionStorage.getItem("selectedCategory");
    return saved || "Dupla Ação";
  });
  const [expandedCategory, setExpandedCategory] = useState(() => {
    const saved = sessionStorage.getItem("expandedCategory");
    return saved || "Atuadores Pneumáticos";
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [page, setPage] = useState(0);

  // const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem("selectedCategory", selectedCategory);
    sessionStorage.setItem("expandedCategory", expandedCategory);
  }, [selectedCategory, expandedCategory]);

  const categories = useMemo(() => {
    if (!products.length) return [];

    const categoryMap: Record<
      string,
      { name: string; subcategories: Set<string> }
    > = {};

    products.forEach((p: Product) => {
      const cat = p.categoria || "Sem Categoria";
      if (!categoryMap[cat]) {
        categoryMap[cat] = { name: cat, subcategories: new Set() };
      }
      if (p.subcategoria) {
        categoryMap[cat].subcategories.add(p.subcategoria);
      }
    });

    return Object.values(categoryMap).map((c) => ({
      name: c.name,
      subcategories: Array.from(c.subcategories),
    }));
  }, [products]);

  useEffect(() => {
    const categoryMapping: Record<string, string> = {
      atuadores: "Atuadores Pneumáticos",
      valvulas: "Válvulas",
      automacao: "Automação e Controle",
      sensores: "Sensores e Gateways para Monitoramento Industrial",
      conexoes: "Conexões e Flanges para Tubulação",
      paineis: "Painéis Elétricos",
    };

    const subcategoryMapping: Record<string, string> = {
      dupla: "Dupla Ação",
      simples: "Simples Ação",
      pneumatico: "Pneumático e outros",
      gaveta: "Válvulas Gaveta",
      globo: "Válvulas Globo",
      retencao: "Válvulas de Retenção",
      esfera: "Valvulas de Esfera",
      borboleta: "Válvulas Borboleta",
      gateways: "Gateways de Comunicação",
      sensores: "Sensores de Monitoramento",
    };

    const searchParams = new URLSearchParams(window.location.search);
    let urlCategory = searchParams.get("categoria");
    let urlSubcategory = searchParams.get("subcategoria");

    if (!urlCategory && window.location.hash.includes("?")) {
      const hashQuery = window.location.hash.split("?")[1];
      const hashParams = new URLSearchParams(hashQuery);
      urlCategory = hashParams.get("categoria");
      urlSubcategory = hashParams.get("subcategoria");
    }

    if (urlCategory) {
      let normalizedCategory =
        categoryMapping[urlCategory.toLowerCase()] || urlCategory;

      if (urlSubcategory) {
        let normalizedSubcategory =
          subcategoryMapping[urlSubcategory.toLowerCase()] || urlSubcategory;

        const categoryRef = categoriesReference.find(
          (cat) => cat.name.toLowerCase() === normalizedCategory.toLowerCase(),
        );

        if (categoryRef && categoryRef.subcategories) {
          const found = categoryRef.subcategories.find(
            (sub) => sub.toLowerCase() === normalizedSubcategory.toLowerCase(),
          );
          if (!found) {
            for (const cat of categoriesReference) {
              if (cat.subcategories) {
                const foundGlobal = cat.subcategories.find(
                  (sub) =>
                    sub.toLowerCase() === normalizedSubcategory.toLowerCase(),
                );
                if (foundGlobal) {
                  normalizedSubcategory = foundGlobal;
                  normalizedCategory = cat.name;
                  break;
                }
              }
            }
          } else {
            normalizedSubcategory = found;
          }
        }
        setSelectedCategory(normalizedSubcategory);
        setExpandedCategory(normalizedCategory);
      } else {
        setSelectedCategory(normalizedCategory);
        setExpandedCategory(normalizedCategory);
      }
      setPage(0);
    }
  }, [categories]);

  const filteredProducts = useMemo(() => {
    return products.filter(
      (product: Product) =>
        product.categoria === selectedCategory ||
        product.subcategoria === selectedCategory,
    );
  }, [products, selectedCategory]);

  const itemsPerPage = 4;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage,
  );

  const toggleCategory = (categoryName: string) => {
    setExpandedCategory((prev) => (prev === categoryName ? "" : categoryName));
  };

  const changeCategory = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setPage(0);
    setMenuOpen(false);
  };

  const handleCategorySelect = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setExpandedCategory(categoryName);
    setShowCategoryGrid(false);
    sessionStorage.setItem("hasVisitedPortfolio", "true");
    setPage(0);
  };

  if (isError) {
    return (
      <div className="flex select-none flex-col p-4 text-center">
        <p className="text-red-400">
          Sinto muito, mas infelizmente ocorreu um erro ao carregar nosso
          portfolio...
        </p>
        <p>Não desanime! Entre em contato conosco abaixo e saiba mais:</p>
      </div>
    );
  }

  return (
    <section
      id="portfolio-previewer"
      className="flex h-auto min-h-screen flex-col bg-gray-100 md:h-[60vh] md:min-h-max md:flex-row"
    >
      <AnimatePresence>
        {showCategoryGrid ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex h-full w-full items-center justify-center"
          >
            <CategoryGrid
              categories={categoriesReference}
              onCategorySelect={handleCategorySelect}
            />
          </motion.div>
        ) : (
          <motion.div
            key="previewer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex w-full flex-col md:flex-row"
          >
            <div className="hidden w-72 flex-col overflow-y-auto shadow-md md:flex">
              <motion.ul
                className="flex flex-col space-y-4 p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {categories.map((category) => (
                  <li key={category.name}>
                    <button
                      onClick={() =>
                        category.subcategories &&
                        category.subcategories.length > 0
                          ? toggleCategory(category.name)
                          : changeCategory(category.name)
                      }
                      className={`flex w-full justify-between rounded-lg px-4 py-2 text-left transition-colors ${
                        selectedCategory === category.name
                          ? "bg-primary-700 text-white"
                          : "bg-white text-gray-700 hover:bg-primary-200"
                      }`}
                    >
                      {category.name}
                      {category.subcategories &&
                        category.subcategories.length > 0 && (
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`transition-transform ${
                              expandedCategory === category.name
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        )}
                    </button>
                    {expandedCategory === category.name &&
                      category.subcategories && (
                        <motion.ul
                          className="ml-4 mt-2 space-y-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          {category.subcategories.map((sub) => (
                            <li key={sub}>
                              <button
                                onClick={() => changeCategory(sub)}
                                className={`w-full rounded-lg px-4 py-2 text-left transition-colors ${
                                  selectedCategory === sub
                                    ? "bg-primary-700 text-white"
                                    : "bg-white text-gray-700 hover:bg-primary-200"
                                }`}
                              >
                                {sub}
                              </button>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                  </li>
                ))}
              </motion.ul>
            </div>

            {menuOpen && (
              <div
                className="fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-50 md:hidden"
                onClick={() => setMenuOpen(false)}
              >
                <div
                  className="max-h-[50%] w-80 overflow-y-auto rounded-lg bg-white p-4 shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ul className="space-y-4">
                    {categories.map((category) => (
                      <li key={category.name}>
                        <button
                          onClick={() =>
                            category.subcategories &&
                            category.subcategories.length > 0
                              ? toggleCategory(category.name)
                              : changeCategory(category.name)
                          }
                          className={`flex w-full justify-between rounded-lg px-4 py-2 text-left transition-colors ${
                            selectedCategory === category.name
                              ? "bg-primary-700 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-primary-200"
                          }`}
                        >
                          {category.name}
                          {category.subcategories &&
                            category.subcategories.length > 0 && (
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`transition-transform ${
                                  expandedCategory === category.name
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                            )}
                        </button>
                        {expandedCategory === category.name &&
                          category.subcategories && (
                            <ul className="ml-4 mt-2 space-y-2">
                              {category.subcategories.map((sub) => (
                                <li key={sub}>
                                  <button
                                    onClick={() => changeCategory(sub)}
                                    className={`w-full rounded-lg px-4 py-2 text-left transition-colors ${
                                      selectedCategory === sub
                                        ? "bg-primary-700 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-primary-200"
                                    }`}
                                  >
                                    {sub}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              className="m-4 block rounded bg-primary-700 px-4 py-2 text-white md:hidden"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              Menu
            </button>

            <div className="flex-1 p-4 md:p-6">
              {isLoading ? (
                <div className="flex h-full w-full items-center justify-center">
                  <l-ring-2
                    size="40"
                    stroke="5"
                    stroke-length="0.25"
                    bg-opacity="0.1"
                    speed="0.8"
                    color="black"
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                    {paginatedProducts.map((item: Product) => (
                      <IndividualProduct
                        key={item.id_produto}
                        item={item}
                        paginatedProducts={paginatedProducts}
                      />
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-center space-x-4">
                    <button
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 0}
                      className={`rounded px-4 py-2 transition-all ${
                        page === 0
                          ? "bg-gray-300"
                          : "bg-primary-700 text-white hover:bg-primary-800"
                      }`}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <span className="text-gray-700">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page === totalPages - 1}
                      className={`rounded px-4 py-2 transition-all ${
                        page === totalPages - 1
                          ? "bg-gray-300"
                          : "bg-primary-700 text-white hover:bg-primary-800"
                      }`}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProductsPreviewer;
